
import { useProjectActions } from "@contexts/projects";
import { useEffect, useMemo, useState } from "react";
import Modal from "@components/Modal";
import NewCompany from "./newCompany";
import NewBuilding from "./newBuilding";
import BuildingFirstRunModal from "./BuildingFirstRunModal";

const CompanyBuildingDirectory = () => {
  const [companies, setCompanies] = useState([])
  const [buildings, setBuildings] = useState([])
  const [loading, setLoading] = useState(false)
  const [showInactive, setShowInactive] = useState(false)
  const [expandedCompanyIds, setExpandedCompanyIds] = useState([])
  const [newCompanyVisible, setNewCompanyVisible] = useState(false)
  const [newBuildingVisible, setNewBuildingVisible] = useState(false)
  const [editingCompany, setEditingCompany] = useState(null)
  const [editingBuilding, setEditingBuilding] = useState(null)
  // Obra escolhida para a primeira execução do pipeline (id do CADASTRO). O botão só existe
  // enquanto `has_measurement` é falso — depois da ingestão ele não tem mais função.
  const [firstRunBuilding, setFirstRunBuilding] = useState(null)
  const [feedback, setFeedback] = useState(null)
  const { listCompanies, getCompany, getBuilding } = useProjectActions();

  const load = async () => {
    setLoading(true)
    setFeedback(null)
    try {
      // `source: 'registry'`: esta tela edita/desativa/remove o CADASTRO da obra, então precisa
      // do `_id` de `buildings` — que é o que getBuilding/updateBuilding/deleteBuilding recebem.
      // As demais telas ficam no default (`wbs_buildings`), cujo `_id` é o que o dashboard usa.
      const res = await listCompanies(undefined, { includeInactive: showInactive, source: 'registry' });
      if (!res?.ok) {
        setFeedback({ ok: false, message: res?.message || 'Não foi possível carregar construtoras e obras.' })
      }
      setCompanies(res?.companies || [])
      setBuildings(res?.buildings || [])
    } catch {
      setCompanies([])
      setBuildings([])
      setFeedback({ ok: false, message: 'Não foi possível carregar construtoras e obras.' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive])

  // Construtoras e obras (dentro de cada construtora) sempre em ordem alfabética.
  const sortedCompanies = useMemo(
    () => [...companies].sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR")),
    [companies],
  )

  const buildingsByCompany = useMemo(() => {
    const map = {}
    for (const building of buildings) {
      const key = `${building.company_id}`
      map[key] = map[key] || []
      map[key].push(building)
    }
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => (
        (a.building_name || a.description || "").localeCompare(b.building_name || b.description || "", "pt-BR")
      ))
    }
    return map
  }, [buildings])

  const toggleCompany = (companyId) => {
    setExpandedCompanyIds(prev => (
      prev.includes(companyId) ? prev.filter(id => id !== companyId) : [...prev, companyId]
    ))
  }

  // A listagem só traz name/tags/erp/subdomain/active (allowlist de listCompanies.js) —
  // editar exige o documento completo, buscado sob demanda só quando o operador clica.
  const handleEditCompany = async (companyId, e) => {
    e.stopPropagation()
    const res = await getCompany(companyId)
    if (res?.ok) {
      setEditingCompany(res.company)
    } else {
      setFeedback({ ok: false, message: res?.message || 'Não foi possível carregar a construtora.' })
    }
  }

  const handleEditBuilding = async (buildingId, e) => {
    e.stopPropagation()
    const res = await getBuilding(buildingId)
    if (res?.ok) {
      setEditingBuilding(res.building)
    } else {
      setFeedback({ ok: false, message: res?.message || 'Não foi possível carregar a obra.' })
    }
  }

  const handleCompanyCreated = async (result) => {
    if (result?.ok) {
      setFeedback({ ok: true, message: result.message })
      setNewCompanyVisible(false)
      setEditingCompany(null)
      await load()
    } else {
      setFeedback({ ok: false, message: result?.message || 'Não foi possível salvar a construtora.' })
    }
  }

  const handleFirstRunDone = async (result) => {
    setFirstRunBuilding(null)
    setFeedback({ ok: !!result?.ok, message: result?.message || 'Execução solicitada.' })
    await load()
  }

  const handleBuildingCreated = async (result) => {
    if (result?.ok) {
      setFeedback({ ok: true, message: result.message })
      setNewBuildingVisible(false)
      setEditingBuilding(null)
      await load()
    } else {
      setFeedback({ ok: false, message: result?.message || 'Não foi possível salvar a obra.' })
    }
  }

  return (
    <div className="directory-page">
      {newCompanyVisible && (
        <Modal close={() => setNewCompanyVisible(false)}>
          <NewCompany onSuccess={handleCompanyCreated} />
        </Modal>
      )}
      {newBuildingVisible && (
        <Modal close={() => setNewBuildingVisible(false)}>
          <NewBuilding onSuccess={handleBuildingCreated} />
        </Modal>
      )}
      {editingCompany && (
        <Modal close={() => setEditingCompany(null)}>
          <NewCompany company={editingCompany} onSuccess={handleCompanyCreated} />
        </Modal>
      )}
      {editingBuilding && (
        <Modal close={() => setEditingBuilding(null)}>
          <NewBuilding building={editingBuilding} onSuccess={handleBuildingCreated} />
        </Modal>
      )}
      {firstRunBuilding && (
        <Modal close={() => setFirstRunBuilding(null)}>
          <BuildingFirstRunModal buildingId={firstRunBuilding} onDone={handleFirstRunDone} />
        </Modal>
      )}

      <div className="directory-header">
        <h2>Construtoras e Obras</h2>
        <div className="directory-actions">
          <label className="show-inactive-toggle">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            Mostrar inativas
          </label>
          <button type="button" className="button button-accent" onClick={() => setNewCompanyVisible(true)}>
            Nova Construtora
          </button>
          <button type="button" className="button button-accent" onClick={() => setNewBuildingVisible(true)}>
            Nova Obra
          </button>
        </div>
      </div>

      {feedback && (
        <p
          className={feedback.ok ? "directory-feedback directory-feedback--ok" : "directory-feedback directory-feedback--error"}
          role={feedback.ok ? "status" : "alert"}
          aria-live={feedback.ok ? "polite" : "assertive"}
        >
          {feedback.message}
        </p>
      )}

      {loading && <p role="status" aria-live="polite">Carregando...</p>}

      {!loading && (
        <div className="directory-list">
          {sortedCompanies.map(company => {
            const companyBuildings = buildingsByCompany[`${company._id}`] || []
            const expanded = expandedCompanyIds.includes(`${company._id}`)
            const companyInactive = company.active === false
            const companyPanelId = `company-buildings-${company._id}`
            return (
              <div className={`directory-company ${companyInactive ? "directory-company--inactive" : ""}`} key={company._id}>
                <div className={`directory-company-row ${expanded ? "expanded" : ""}`}>
                  <button
                    type="button"
                    className="directory-company-toggle"
                    aria-expanded={expanded}
                    aria-controls={companyPanelId}
                    aria-label={`${expanded ? 'Recolher' : 'Expandir'} obras de ${company.name}`}
                    onClick={() => toggleCompany(`${company._id}`)}
                  >
                    <span className="directory-company-name">
                      {company.name}
                      {companyInactive && <span className="inactive-tag">Inativa</span>}
                    </span>
                    {company.erp && <span className="directory-company-erp">{company.erp}</span>}
                    <span className="directory-company-count">{companyBuildings.length} obra(s)</span>
                  </button>
                  <button type="button" className="button button-accent" onClick={(e) => handleEditCompany(company._id, e)}>
                    Editar
                  </button>
                </div>

                {expanded && (
                  <div id={companyPanelId}>
                    {companyBuildings.length === 0 ? (
                      <p className="directory-empty">Nenhuma obra cadastrada para essa construtora.</p>
                    ) : (
                      <table className="directory-buildings">
                        <thead>
                          <tr>
                            <th>Obra</th>
                            <th>Cidade/UF</th>
                            <th>Status EAP/Medição</th>
                            <th>Última medição</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {companyBuildings.map(building => {
                          const buildingInactive = building.active === false
                          const locationValue = [building.city, building.state].filter(Boolean).join('/')
                          const locationComplete = (
                            building.location_complete === true && building.city && building.state
                          )
                          return (
                            <tr key={building._id} className={buildingInactive ? "directory-building--inactive" : ""}>
                              <td>
                                {building.building_name || building.description}
                                {buildingInactive && <span className="inactive-tag">Inativa</span>}
                              </td>
                              <td>
                                {locationComplete ? locationValue : (
                                  <>
                                    {locationValue && <><span>{locationValue}</span>{' — '}</>}
                                    <span className="location-pending">Localização pendente</span>
                                  </>
                                )}
                              </td>
                              <td>
                                {building.has_measurement
                                  ? 'OK'
                                  : 'Pendente de primeira ingestão'}
                              </td>
                              <td>
                                {building.last_measurement_date
                                  ? new Date(building.last_measurement_date).toLocaleDateString()
                                  : '-'}
                              </td>
                              <td className="directory-buildings-actions">
                                {/* Some depois da primeira ingestão: `has_measurement` vira
                                    true e a obra passa a ser atualizada pelo fluxo normal
                                    do dashboard, não mais por aqui. */}
                                {!building.has_measurement && (
                                  <button
                                    type="button"
                                    className="button"
                                    onClick={(e) => { e.stopPropagation(); setFirstRunBuilding(`${building._id}`) }}
                                  >
                                    Registrar dados
                                  </button>
                                )}
                                <button type="button" className="button button-accent" onClick={(e) => handleEditBuilding(building._id, e)}>
                                  Editar
                                </button>
                              </td>
                            </tr>
                          )
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  );
};

export default CompanyBuildingDirectory;
